<!--
  A wrapper component for a checkbox input and label, supporting v-model using the defineModel macro.
  - Works with both boolean and array (for checkbox groups) v-models.
  - Props:
      - id: string (required) - The unique id for the input and label.
      - label: string (required) - The label text or HTML for the checkbox.
      - value: number | string (optional) - The value for the checkbox when using array v-model.
  - Emits:
      - input: Emits the checked state (boolean) on change.
      - checkBoxInputChange: Emits an object with isChecked and id on change.
  - Keyboard accessible: Space and Enter on the label toggle the checkbox.
  - Usage:
      <Checkbox
        :id="'my-checkbox'"
        label="My Checkbox"
        :value="'foo'"
        v-model="selectedValues"
      />
-->
<template>
  <div class="form-checkbox">
    <input
      class="checkbox-input"
      ref="checkboxRef"
      type="checkbox"
      :id="id"
      @change="emitInput()"
      v-bind="inputValueAttr"
      :checked="isChecked"
      tabindex="-1"
      v-model="modelValue"
    />
    <label
      :aria-label="label"
      @keydown.enter.prevent="handleKeydown($event)"
      @keydown.space.prevent="handleKeydown($event)"
      :for="id"
      tabindex="0"
      v-html="label"
    ></label>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

const props = defineProps<{
  id: string;
  label: string;
  value?: number | string;
}>();

const checkboxRef = ref<HTMLInputElement | null>(null);

const modelValue = defineModel<(typeof props.value)[] | boolean>();
const inputValueAttr = computed(() => (props.value !== undefined ? { value: props.value } : {}));
const isChecked = computed(() => {
  if (Array.isArray(modelValue.value)) {
    return modelValue.value?.includes(props.value);
  } else {
    return modelValue.value;
  }
});

const emit = defineEmits(['input', 'checkBoxInputChange']);

const emitInput = () => {
  const isChecked = checkboxRef.value?.checked;
  emit('input', isChecked);
  emit('checkBoxInputChange', {
    isChecked: isChecked,
    id: props.id,
  });
};
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    checkboxRef.value?.click();
  }
};
</script>
